import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";
import { getStudents } from "./userService";
import { getAllColleges } from "./collegeService";
import { getAllSalaryConfigs, getAllSalaryRecords } from "./salaryService";
import { getAllFeeAmounts, FEE_TYPES } from "./feeConfigService";
import { getAllPayments, PAYMENT_STATUS as FEE_PAYMENT_STATUS } from "./studentFeeService";
import { STUDENT_LEVELS } from "../utils";

// Revenue Categories
export const INCOME_CATEGORIES = {
  TRAINING_FEE: "training_fee",
  INTERNSHIP_FEE: "internship_fee", 
  COLLEGE_FEE: "college_fee",
  CERTIFICATION_FEE: "certification_fee",
  CONSULTATION: "consultation",
  OTHER: "other",
};

export const EXPENSE_CATEGORIES = {
  TUTOR_SALARY: "tutor_salary",
  EMPLOYEE_SALARY: "employee_salary",
  INTERN_STIPEND: "intern_stipend",
  INFRASTRUCTURE: "infrastructure",
  UTILITIES: "utilities",
  MARKETING: "marketing",
  OFFICE_SUPPLIES: "office_supplies",
  SOFTWARE_LICENSES: "software_licenses",
  EQUIPMENT: "equipment",
  TRAVEL: "travel",
  OTHER: "other",
};

export const TRANSACTION_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  REFUNDED: "refunded",
};

export const PAYMENT_METHODS = {
  CASH: "cash",
  BANK_TRANSFER: "bank_transfer",
  UPI: "upi",
  CREDIT_CARD: "credit_card",
  DEBIT_CARD: "debit_card",
  CHEQUE: "cheque",
};

// Default fee structure - now fetched from database
export const DEFAULT_FEES = {
  PAID_TRAINEE: 25000, // Fallback values
  INTERNSHIP_NON_COLLEGE: 15000,
  INDIVIDUAL_INTERN_STIPEND: 2000,
  // These will be replaced by configurable values
};

// Get Calculated Income Records (from students and colleges)
export const getCalculatedIncomeRecords = async (filters = {}) => {
  try {
    const incomeRecords = [];
    
    // Get actual student fee payments
    let studentPayments = [];
    try {
      studentPayments = await getAllPayments(filters) || [];
    } catch (error) {
      console.warn("Could not fetch student payments:", error);
      studentPayments = [];
    }
    
    // Add actual student fee payments to income records
    studentPayments.forEach(payment => {
      incomeRecords.push({
        id: `payment_${payment.id}`,
        category: INCOME_CATEGORIES.TRAINING_FEE,
        amount: payment.amount,
        description: `Fee payment from ${payment.studentName} (Installment ${payment.installmentNumber})`,
        studentName: payment.studentName,
        studentEmail: payment.studentEmail,
        courseName: "Training Program",
        date: payment.paymentDate || new Date(),
        paymentMethod: payment.paymentMethod,
        status: TRANSACTION_STATUS.COMPLETED,
        type: "income",
        transactionId: payment.transactionId,
        installmentNumber: payment.installmentNumber,
        createdAt: payment.createdAt || new Date(),
        updatedAt: new Date(),
      });
    });
    
    // Get configurable fee amounts
    let feeAmounts = {};
    try {
      feeAmounts = await getAllFeeAmounts();
    } catch (error) {
      console.warn("Could not fetch fee configurations, using defaults:", error);
      feeAmounts = {
        [FEE_TYPES.PAID_TRAINEE]: DEFAULT_FEES.PAID_TRAINEE,
        [FEE_TYPES.INDIVIDUAL_INTERNSHIP]: DEFAULT_FEES.INTERNSHIP_NON_COLLEGE,
      };
    }
    
    // Get students and colleges data with error handling
    let students = [];
    let colleges = [];
    
    try {
      students = await getStudents() || [];
    } catch (error) {
      console.warn("Could not fetch students:", error);
      students = [];
    }
    
    try {
      colleges = await getAllColleges() || [];
    } catch (error) {
      console.warn("Could not fetch colleges:", error);
      colleges = [];
    }

    // Note: We're now using actual payments instead of calculating from student levels
    // The code below is kept for backward compatibility or reference purposes
    
    // Process interns (keep this for college-based fees which work differently)
    const interns = students.filter(student => 
      student.studentLevel === STUDENT_LEVELS.INTERN
    );

    // Create a map of college fees
    const collegeFeeMap = {};
    colleges.forEach(college => {
      if (college.internshipFee) {
        collegeFeeMap[college.id] = college.internshipFee;
      }
    });

    interns.forEach(student => {
      let amount = feeAmounts[FEE_TYPES.INDIVIDUAL_INTERNSHIP] || DEFAULT_FEES.INTERNSHIP_NON_COLLEGE;
      let category = INCOME_CATEGORIES.INTERNSHIP_FEE;
      let description = `Internship fee for ${student.name}`;

      // Check if student is assigned to a college
      const collegeAssignment = student.collegeId;
      if (collegeAssignment && collegeFeeMap[collegeAssignment]) {
        amount = collegeFeeMap[collegeAssignment];
        category = INCOME_CATEGORIES.COLLEGE_FEE;
        description = `College internship fee for ${student.name}`;
      }

      incomeRecords.push({
        id: `internship_${student.id}`,
        category,
        amount,
        description,
        studentName: student.name,
        studentEmail: student.email,
        courseName: student.courses?.map(c => c.name).join(", ") || "Internship Program",
        collegeId: collegeAssignment,
        date: student.enrollmentDate ? new Date(student.enrollmentDate) : new Date(),
        paymentMethod: PAYMENT_METHODS.BANK_TRANSFER,
        status: TRANSACTION_STATUS.COMPLETED,
        type: "income",
        createdAt: student.createdAt || new Date(),
        updatedAt: new Date(),
      });
    });

    // Apply filters
    let filteredRecords = incomeRecords;

    if (filters.category) {
      filteredRecords = filteredRecords.filter(record => record.category === filters.category);
    }

    if (filters.status) {
      filteredRecords = filteredRecords.filter(record => record.status === filters.status);
    }

    if (filters.startDate && filters.endDate) {
      filteredRecords = filteredRecords.filter(record => {
        const recordDate = record.date;
        return recordDate >= filters.startDate && recordDate <= filters.endDate;
      });
    }

    // Sort by date (newest first)
    filteredRecords.sort((a, b) => b.date - a.date);

    if (filters.limit) {
      filteredRecords = filteredRecords.slice(0, filters.limit);
    }

    return filteredRecords;
  } catch (error) {
    console.error("Error calculating income records:", error);
    toast.error("Failed to calculate income records");
    return []; // Return empty array instead of throwing
  }
};

// Get Calculated Expense Records (from salary configurations)
export const getCalculatedExpenseRecords = async (filters = {}) => {
  try {
    const expenseRecords = [];
    
    // Get configurable fee amounts
    let feeAmounts = {};
    try {
      feeAmounts = await getAllFeeAmounts();
    } catch (error) {
      console.warn("Could not fetch fee configurations, using defaults:", error);
      feeAmounts = {
        [FEE_TYPES.INDIVIDUAL_INTERN_STIPEND]: DEFAULT_FEES.INDIVIDUAL_INTERN_STIPEND,
      };
    }
    
    // Get salary records for current month or filtered period
    const currentDate = new Date();
    const month = filters.month || currentDate.getMonth() + 1;
    const year = filters.year || currentDate.getFullYear();

    // Get salary records
    let salaryRecords = [];
    try {
      salaryRecords = await getAllSalaryRecords({ month, year }) || [];
    } catch (error) {
      console.warn("Could not fetch salary records:", error);
      salaryRecords = [];
    }

    // Get students for individual intern stipends
    let students = [];
    try {
      students = await getStudents() || [];
    } catch (error) {
      console.warn("Could not fetch students:", error);
      students = [];
    }

    // Process salary records as expenses (tutors and employees)
    salaryRecords.forEach(salaryRecord => {
      const category = salaryRecord.userRole === 'tutor' 
        ? EXPENSE_CATEGORIES.TUTOR_SALARY 
        : EXPENSE_CATEGORIES.EMPLOYEE_SALARY;

      expenseRecords.push({
        id: `salary_${salaryRecord.id}`,
        category,
        amount: salaryRecord.netSalary || 0,
        description: `${salaryRecord.userRole === 'tutor' ? 'Tutor' : 'Employee'} salary for ${salaryRecord.userName} - ${month}/${year}`,
        tutorName: salaryRecord.userRole === 'tutor' ? salaryRecord.userName : null,
        employeeName: salaryRecord.userRole !== 'tutor' ? salaryRecord.userName : null,
        vendorName: salaryRecord.userName,
        invoiceNumber: `SAL-${salaryRecord.id}`,
        referenceId: salaryRecord.id,
        date: new Date(year, month - 1, salaryRecord.paymentDate || 30),
        paymentMethod: PAYMENT_METHODS.BANK_TRANSFER,
        status: salaryRecord.status === 'paid' 
          ? TRANSACTION_STATUS.COMPLETED 
          : TRANSACTION_STATUS.PENDING,
        type: "expense",
        createdAt: salaryRecord.createdAt || new Date(),
        updatedAt: salaryRecord.updatedAt || new Date(),
      });
    });

    // Process individual intern stipends (only for interns not assigned to colleges)
    const individualInterns = students.filter(student => 
      student.studentLevel === STUDENT_LEVELS.INTERN && !student.collegeId
    );

    const stipendAmount = feeAmounts[FEE_TYPES.INDIVIDUAL_INTERN_STIPEND] || DEFAULT_FEES.INDIVIDUAL_INTERN_STIPEND;

    individualInterns.forEach(intern => {
      expenseRecords.push({
        id: `stipend_${intern.id}`,
        category: EXPENSE_CATEGORIES.INTERN_STIPEND,
        amount: stipendAmount,
        description: `Monthly stipend for individual intern ${intern.name} - ${month}/${year}`,
        internName: intern.name,
        vendorName: intern.name,
        invoiceNumber: `STIPEND-${intern.id}`,
        referenceId: intern.id,
        date: new Date(year, month - 1, 30),
        paymentMethod: PAYMENT_METHODS.BANK_TRANSFER,
        status: TRANSACTION_STATUS.COMPLETED,
        type: "expense",
        createdAt: intern.createdAt || new Date(),
        updatedAt: new Date(),
      });
    });

    // Apply filters
    let filteredRecords = expenseRecords;

    if (filters.category) {
      filteredRecords = filteredRecords.filter(record => record.category === filters.category);
    }

    if (filters.status) {
      filteredRecords = filteredRecords.filter(record => record.status === filters.status);
    }

    if (filters.startDate && filters.endDate) {
      filteredRecords = filteredRecords.filter(record => {
        const recordDate = record.date;
        return recordDate >= filters.startDate && recordDate <= filters.endDate;
      });
    }

    // Sort by date (newest first)
    filteredRecords.sort((a, b) => b.date - a.date);

    if (filters.limit) {
      filteredRecords = filteredRecords.slice(0, filters.limit);
    }

    return filteredRecords;
  } catch (error) {
    console.error("Error calculating expense records:", error);
    toast.error("Failed to calculate expense records");
    return []; // Return empty array instead of throwing
  }
};

// Get Revenue Statistics from calculated data
export const getRevenueStatistics = async (filters = {}) => {
  try {
    const currentDate = new Date();
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    
    const defaultFilters = {
      startDate: startOfMonth,
      endDate: endOfMonth,
      month: currentDate.getMonth() + 1,
      year: currentDate.getFullYear(),
      ...filters,
    };

    // Get calculated income and expense data
    const [incomeRecords, expenseRecords] = await Promise.all([
      getCalculatedIncomeRecords(defaultFilters),
      getCalculatedExpenseRecords(defaultFilters),
    ]);

    // Calculate totals
    const totalIncome = incomeRecords
      .filter(record => record.status === TRANSACTION_STATUS.COMPLETED)
      .reduce((sum, record) => sum + record.amount, 0);

    const totalExpenses = expenseRecords
      .filter(record => record.status === TRANSACTION_STATUS.COMPLETED)
      .reduce((sum, record) => sum + record.amount, 0);

    const netProfit = totalIncome - totalExpenses;
    const profitMargin = totalIncome > 0 ? (netProfit / totalIncome) * 100 : 0;

    // Income by category
    const incomeByCategory = {};
    incomeRecords.forEach(record => {
      if (record.status === TRANSACTION_STATUS.COMPLETED) {
        incomeByCategory[record.category] = (incomeByCategory[record.category] || 0) + record.amount;
      }
    });

    // Expenses by category
    const expensesByCategory = {};
    expenseRecords.forEach(record => {
      if (record.status === TRANSACTION_STATUS.COMPLETED) {
        expensesByCategory[record.category] = (expensesByCategory[record.category] || 0) + record.amount;
      }
    });

    // Transaction counts
    const totalIncomeTransactions = incomeRecords.length;
    const totalExpenseTransactions = expenseRecords.length;
    const pendingIncomeTransactions = incomeRecords.filter(r => r.status === TRANSACTION_STATUS.PENDING).length;
    const pendingExpenseTransactions = expenseRecords.filter(r => r.status === TRANSACTION_STATUS.PENDING).length;

    // Average transaction amounts
    const avgIncomeTransaction = totalIncomeTransactions > 0 ? totalIncome / totalIncomeTransactions : 0;
    const avgExpenseTransaction = totalExpenseTransactions > 0 ? totalExpenses / totalExpenseTransactions : 0;

    // Student type breakdown
    const students = await getStudents();
    const studentBreakdown = {
      freeTrainees: students.filter(s => s.studentLevel === STUDENT_LEVELS.FREE_TRAINEE).length,
      paidTrainees: students.filter(s => s.studentLevel === STUDENT_LEVELS.PAID_TRAINEE).length,
      collegeInterns: students.filter(s => s.studentLevel === STUDENT_LEVELS.INTERN && s.collegeId).length,
      individualInterns: students.filter(s => s.studentLevel === STUDENT_LEVELS.INTERN && !s.collegeId).length,
      employees: students.filter(s => s.studentLevel === STUDENT_LEVELS.EMPLOYEE).length,
    };

    return {
      totalIncome,
      totalExpenses,
      netProfit,
      profitMargin,
      incomeByCategory,
      expensesByCategory,
      totalIncomeTransactions,
      totalExpenseTransactions,
      pendingIncomeTransactions,
      pendingExpenseTransactions,
      avgIncomeTransaction,
      avgExpenseTransaction,
      studentBreakdown,
      period: {
        startDate: defaultFilters.startDate,
        endDate: defaultFilters.endDate,
      },
    };
  } catch (error) {
    console.error("Error fetching revenue statistics:", error);
    toast.error("Failed to fetch revenue statistics");
    throw error;
  }
};

// Get Monthly Revenue Trends (last 12 months) from calculated data
export const getMonthlyRevenueTrends = async () => {
  try {
    const trends = [];
    const currentDate = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const startOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const endOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
      
      const filters = {
        startDate: startOfMonth,
        endDate: endOfMonth,
        month: monthDate.getMonth() + 1,
        year: monthDate.getFullYear(),
      };

      const [incomeRecords, expenseRecords] = await Promise.all([
        getCalculatedIncomeRecords(filters),
        getCalculatedExpenseRecords(filters),
      ]);

      const monthlyIncome = incomeRecords
        .filter(record => record.status === TRANSACTION_STATUS.COMPLETED)
        .reduce((sum, record) => sum + record.amount, 0);

      const monthlyExpenses = expenseRecords
        .filter(record => record.status === TRANSACTION_STATUS.COMPLETED)
        .reduce((sum, record) => sum + record.amount, 0);

      trends.push({
        month: monthDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
        income: monthlyIncome,
        expenses: monthlyExpenses,
        profit: monthlyIncome - monthlyExpenses,
      });
    }

    return trends;
  } catch (error) {
    console.error("Error fetching monthly revenue trends:", error);
    toast.error("Failed to fetch revenue trends");
    throw error;
  }
};

// Generate Revenue Report from calculated data
export const generateRevenueReport = async (startDate, endDate) => {
  try {
    const filters = {
      startDate,
      endDate,
      month: startDate.getMonth() + 1,
      year: startDate.getFullYear(),
    };

    const [incomeRecords, expenseRecords, statistics] = await Promise.all([
      getCalculatedIncomeRecords(filters),
      getCalculatedExpenseRecords(filters),
      getRevenueStatistics(filters),
    ]);

    return {
      period: { startDate, endDate },
      summary: statistics,
      incomeRecords,
      expenseRecords,
      generatedAt: new Date(),
    };
  } catch (error) {
    console.error("Error generating revenue report:", error);
    toast.error("Failed to generate revenue report");
    throw error;
  }
};

// Update College Fee Configuration
export const updateCollegeFeeConfiguration = async (collegeId, internshipFee) => {
  try {
    const collegeDoc = doc(db, "colleges", collegeId);
    await updateDoc(collegeDoc, {
      internshipFee: parseFloat(internshipFee),
      updatedAt: serverTimestamp(),
    });

    toast.success("College fee configuration updated successfully");
  } catch (error) {
    console.error("Error updating college fee:", error);
    toast.error("Failed to update college fee configuration");
    throw error;
  }
};

// Manual Income/Expense Records (for additional entries not covered by automatic calculation)
export const addManualIncomeRecord = async (incomeData) => {
  try {
    const docRef = await addDoc(collection(db, "revenue_income_manual"), {
      ...incomeData,
      type: "income",
      amount: parseFloat(incomeData.amount),
      date: incomeData.date || Timestamp.now(),
      status: incomeData.status || TRANSACTION_STATUS.COMPLETED,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    toast.success("Manual income record added successfully");
    return docRef.id;
  } catch (error) {
    console.error("Error adding manual income record:", error);
    toast.error("Failed to add manual income record");
    throw error;
  }
};

export const addManualExpenseRecord = async (expenseData) => {
  try {
    const docRef = await addDoc(collection(db, "revenue_expenses_manual"), {
      ...expenseData,
      type: "expense",
      amount: parseFloat(expenseData.amount),
      date: expenseData.date || Timestamp.now(),
      status: expenseData.status || TRANSACTION_STATUS.COMPLETED,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    toast.success("Manual expense record added successfully");
    return docRef.id;
  } catch (error) {
    console.error("Error adding manual expense record:", error);
    toast.error("Failed to add manual expense record");
    throw error;
  }
};

// Format Currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

// Format Percentage
export const formatPercentage = (value) => {
  return `${(value || 0).toFixed(1)}%`;
};

// Get Category Display Name
export const getCategoryDisplayName = (category, type = 'income') => {
  const categories = type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  const categoryKey = Object.keys(categories).find(key => categories[key] === category);
  return categoryKey ? categoryKey.replace(/_/g, ' ').toUpperCase() : category;
};